/**
 * Relative geometry between two aircraft: range, angle off the nose, aspect,
 * and the intercept solution a gun (or an AI's lead turn) needs.
 *
 * Everything here is frame-honest about the engine's convention (math3d.ts):
 * right-handed, Y up, body forward is −Z, body right is +X. "Bearing" below is
 * the target's azimuth in the observer's *body* frame — positive to the right —
 * which is what a HUD arrow and a roll command both want.
 */

import {
  BODY_FORWARD,
  BODY_UP,
  addVec3,
  dotVec3,
  inverseRotateVec3,
  lengthVec3,
  normalizeVec3,
  rotateVec3,
  scaleVec3,
  type Quat,
  type Vec3,
} from "../flight/math3d.ts";
import type { SimState } from "../flight/state.ts";

/** The part of an aircraft's state combat geometry needs — pose plus motion. */
export interface CombatBody {
  readonly position: Vec3;
  readonly velocity: Vec3;
  readonly orientation: Quat;
}

export function bodyOf(sim: SimState): CombatBody {
  return { position: sim.position, velocity: sim.velocity, orientation: sim.orientation };
}

export function noseOf(orientation: Quat): Vec3 {
  return rotateVec3(orientation, BODY_FORWARD);
}

export function subtractVec3(a: Vec3, b: Vec3): Vec3 {
  return { x: a.x - b.x, y: a.y - b.y, z: a.z - b.z };
}

/** Angle between two directions, radians, clamped against floating-point overshoot. */
export function angleBetween(a: Vec3, b: Vec3): number {
  const lengths = lengthVec3(a) * lengthVec3(b);
  if (lengths < 1e-9) return 0;
  return Math.acos(Math.min(1, Math.max(-1, dotVec3(a, b) / lengths)));
}

export interface RelativeGeometry {
  readonly rangeM: number;
  /** Angle between the observer's nose and the line of sight. 0 = dead ahead. */
  readonly angleOffNoseRad: number;
  /** Azimuth of the target in the observer's body frame, positive right, in (−π, π]. */
  readonly bearingRad: number;
  /** Elevation of the target in the observer's body frame, positive up. */
  readonly elevationRad: number;
  /**
   * Where the observer sits relative to the *target's* nose: 0 = the observer
   * is dead ahead of it, π = the observer is on its tail. This is the number
   * that says "I am in a firing position", not `angleOffNoseRad`.
   */
  readonly aspectRad: number;
  /** Positive when the gap is closing. */
  readonly closureMps: number;
  /** Target's altitude minus the observer's. */
  readonly heightDifferenceM: number;
}

export function relativeGeometry(observer: CombatBody, target: CombatBody): RelativeGeometry {
  const offset = subtractVec3(target.position, observer.position);
  const rangeM = lengthVec3(offset);
  const lineOfSight = normalizeVec3(offset, BODY_FORWARD);
  const local = inverseRotateVec3(observer.orientation, lineOfSight);
  const relativeVelocity = subtractVec3(target.velocity, observer.velocity);

  return {
    rangeM,
    angleOffNoseRad: angleBetween(noseOf(observer.orientation), offset),
    bearingRad: Math.atan2(local.x, -local.z),
    elevationRad: Math.asin(Math.min(1, Math.max(-1, local.y))),
    aspectRad: angleBetween(noseOf(target.orientation), scaleVec3(offset, -1)),
    closureMps: -dotVec3(relativeVelocity, lineOfSight),
    heightDifferenceM: target.position.y - observer.position.y,
  };
}

/**
 * Time for a projectile leaving the origin at `projectileSpeedMps` to meet a
 * target that starts at `relativePosition` and holds `targetVelocity`. Solves
 * `|rp + tv·t| = v·t` exactly and returns the smallest positive root, or `null`
 * when the target simply cannot be caught.
 *
 * Both vectors must be expressed in the frame the projectile's *speed* is
 * measured in — see `aimPoint`, which is where getting that wrong showed up.
 */
export function interceptTimeS(
  relativePosition: Vec3,
  targetVelocity: Vec3,
  projectileSpeedMps: number,
): number | null {
  const a = dotVec3(targetVelocity, targetVelocity) - projectileSpeedMps * projectileSpeedMps;
  const b = 2 * dotVec3(relativePosition, targetVelocity);
  const c = dotVec3(relativePosition, relativePosition);
  if (Math.abs(a) < 1e-6) {
    if (Math.abs(b) < 1e-9) return null;
    const linear = -c / b;
    return linear > 0 ? linear : null;
  }
  const discriminant = b * b - 4 * a * c;
  if (discriminant < 0) return null;
  const root = Math.sqrt(discriminant);
  const candidates = [(-b - root) / (2 * a), (-b + root) / (2 * a)].filter((value) => value > 0);
  if (candidates.length === 0) return null;
  return Math.min(...candidates);
}

export interface AimPoint {
  /** Where to point the guns, in world space. */
  readonly point: Vec3;
  readonly timeOfFlightS: number;
  /** True when the intercept is a real solution rather than the fallback "aim at it now". */
  readonly solved: boolean;
}

/**
 * The lead point for a shot, degrading gracefully to pure pursuit when no
 * intercept exists.
 *
 * **World frame, not the shooter's.** A round in this port leaves the muzzle
 * and flies at `gunMuzzleSpeedMps` through the world along the gun line
 * (`rounds.ts`) — it does not carry the aeroplane's own velocity with it — so
 * the solution has to be built from the target's *absolute* velocity. Solving
 * it in the shooter's frame instead (with the relative velocity, which is what
 * this did until 2026-08-20) leaves the aim point out by `shooterVelocity ×
 * timeOfFlight`: about 8.5 m for a scout at 45 m/s shooting at 150 m, against a
 * target sphere of 4.2 m. It was invisible while a hit was a probability
 * sampled from a cone and became the dominant miss the moment rounds started
 * having to actually pass through the target — and it inverted the skill
 * ladder, because `leadQuality` blends *toward* the lead point, so the aces
 * applying all of it shot worst of the four.
 */
export function aimPoint(shooter: CombatBody, target: CombatBody, projectileSpeedMps: number): AimPoint {
  const relativePosition = subtractVec3(target.position, shooter.position);
  const time = interceptTimeS(relativePosition, target.velocity, projectileSpeedMps);
  if (time === null) {
    return { point: target.position, timeOfFlightS: 0, solved: false };
  }
  return {
    point: addVec3(target.position, scaleVec3(target.velocity, time)),
    timeOfFlightS: time,
    solved: true,
  };
}

/**
 * Which face of the target the fire is arriving on (AD-007d's "gunfire and flak
 * distribute hits by aspect"). Classified by the dominant axis of the shooter's
 * position in the target's own body frame, so it is orientation-correct in a
 * bank or inverted.
 */
export const HIT_ASPECTS = ["nose", "tail", "left", "right", "top", "belly"] as const;

export type HitAspect = (typeof HIT_ASPECTS)[number];

/** The face a vector pointing *away* from the target, in its own body frame, lands on. */
function aspectOfLocal(local: Vec3): HitAspect {
  const forward = -local.z;
  const right = local.x;
  const up = local.y;
  const magnitudes = [Math.abs(forward), Math.abs(right), Math.abs(up)];
  const dominant = magnitudes.indexOf(Math.max(...magnitudes));
  if (dominant === 0) return forward >= 0 ? "nose" : "tail";
  if (dominant === 1) return right >= 0 ? "right" : "left";
  return up >= 0 ? "top" : "belly";
}

export function hitAspect(target: CombatBody, shooterPosition: Vec3): HitAspect {
  return aspectOfLocal(inverseRotateVec3(target.orientation, subtractVec3(shooterPosition, target.position)));
}

/**
 * The face a round arriving along `incoming` struck. Sharper than `hitAspect`
 * for a real projectile: a round that was fired from dead astern but crossed to
 * the far side before it got there arrives on the *far* face, and where its
 * shooter was standing has nothing to do with it.
 */
export function arrivalAspect(target: CombatBody, incoming: Vec3): HitAspect {
  return aspectOfLocal(inverseRotateVec3(target.orientation, scaleVec3(incoming, -1)));
}

/**
 * The world-space muzzle position: a body-frame offset from the pose origin,
 * rotated by the aircraft's attitude. Takes the whole offset rather than a
 * forward/lateral pair because the `.DAT` gun block places a muzzle in all
 * three axes — a Gotha's ventral gun is 0.74 m *below* the origin.
 */
export function muzzlePosition(body: CombatBody, bodyOffsetM: Vec3): Vec3 {
  return addVec3(body.position, rotateVec3(body.orientation, bodyOffsetM));
}

/** Unit "up" of the airframe, for a renderer that wants a wing-plane reference. */
export function upOf(orientation: Quat): Vec3 {
  return rotateVec3(orientation, BODY_UP);
}
